"use client"

import { useEffect, useState } from "react"

/**
 * Shared scale-to-fit math for the sitewide fixed-corner ad widgets
 * (StickyBannerAd, InPagePushAd, InstantMessageAd, VideoSliderAd) — same
 * "render the iframe at true native size, CSS-transform: scale() it down,
 * size the WRAPPER to the scaled result" approach NetworkDisplayAd.tsx uses
 * for column-width ads (see that file's history comment for why this exists
 * at all: a fixed-size iframe forced into a smaller box any other way either
 * overflows with scrollbars or gets silently clipped, never proportionally
 * shrunk).
 *
 * Found live: on a real mobile viewport, these widgets' desktop-only native
 * sizes (300-400px) run right up against or past the whole screen width,
 * one on top of another. Just shrinking the WRAPPER's CSS width/height
 * (without also scaling the iframe) crops the creative instead of shrinking
 * it — the exact NetworkDisplayAd bug this codebase already fixed once.
 * Just applying `transform: scale()` alone (without also shrinking the
 * wrapper's real layout box) leaves an invisible oversized hit-box behind —
 * `pointer-events-auto` on a box still laid out at the old native size would
 * keep blocking clicks/scroll on whatever's underneath even though the ad
 * visually looks smaller. This hook does both together, correctly.
 *
 * marginPx: how much horizontal breathing room to always leave against the
 * viewport edge (these are corner-pinned, so only one edge needs it).
 *
 * maxViewportFraction: the real fix for mobile — margin alone only stops
 * horizontal OVERFLOW, it doesn't stop a widget from just being huge
 * relative to a small screen. Confirmed live: a 380px-native ad on a 390px
 * phone only scales to ~0.98 under margin alone (374px, barely smaller),
 * still visually swallowing almost the entire screen and overlapping the
 * header/other widgets. Capping to a FRACTION of the viewport (default
 * 55%) forces a real, proportionate shrink on small screens while staying
 * a no-op on desktop, where even a 400px native size is nowhere near 55%
 * of a normal browser window.
 */
export function useFixedCornerAdScale(
  nativeWidth: number,
  nativeHeight: number,
  marginPx = 16,
  maxViewportFraction = 0.55
) {
  const [viewportWidth, setViewportWidth] = useState<number | null>(null)
  const [viewportHeight, setViewportHeight] = useState<number | null>(null)

  useEffect(() => {
    const update = () => {
      setViewportWidth(window.innerWidth)
      setViewportHeight(window.innerHeight)
    }
    update()
    window.addEventListener("resize", update)
    return () => window.removeEventListener("resize", update)
  }, [])

  // Never upscale past 1:1 — same rule NetworkDisplayAd follows. Before the
  // real viewport width is known (first render, SSR), assume full size
  // rather than flashing a shrunk-then-growing layout jump.
  const scale = viewportWidth && viewportHeight
    ? Math.min(
        1,
        Math.max(1, viewportWidth - marginPx) / nativeWidth,
        Math.max(1, viewportHeight - marginPx) / nativeHeight,
        (viewportWidth * maxViewportFraction) / nativeWidth,
        (viewportHeight * maxViewportFraction) / nativeHeight,
      )
    : 1

  return {
    scale,
    scaledWidth: Math.round(nativeWidth * scale),
    scaledHeight: Math.round(nativeHeight * scale),
  }
}
